"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import type { TooltipProps } from "@mui/material";

/**
 * Drop-in replacement for MUI `Tooltip` that closes as soon as anything scrolls.
 *
 * Without this, a tooltip opened on a table cell stays pinned to the cell while the table scrolls,
 * drifts outside the table and only disappears after the hover state times out.
 *
 * Works for both uncontrolled use and controlled use (`open` + `onClose`). Scrolling inside the
 * tooltip itself (long lists) is ignored.
 */
export function ScrollAwareTooltip(props: TooltipProps) {
  const { open: controlledOpen, onOpen, onClose } = props;
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  // True once a scroll closed the tooltip: skip the exit fade so it never lingers (and drifts) while scrolling.
  const [closedByScroll, setClosedByScroll] = useState(false);
  const isOpen = isControlled ? Boolean(controlledOpen) : internalOpen;

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event: Event) => {
      const target = event.target as Element | null;
      if (target && typeof target.closest === "function" && target.closest(".MuiTooltip-popper")) return;
      setClosedByScroll(true);
      if (!isControlled) setInternalOpen(false);
      onClose?.(event as unknown as React.SyntheticEvent<Element, Event>);
    };
    // Capture phase so scrolls on any nested container (table body, page, dialog) are caught.
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("wheel", handleScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("wheel", handleScroll, true);
    };
  }, [isOpen, isControlled, onClose]);

  const handleOpen = useCallback(
    (event: React.SyntheticEvent) => {
      setClosedByScroll(false);
      if (!isControlled) setInternalOpen(true);
      onOpen?.(event);
    },
    [isControlled, onOpen]
  );

  const handleClose = useCallback(
    (event: React.SyntheticEvent | Event) => {
      if (!isControlled) setInternalOpen(false);
      onClose?.(event as React.SyntheticEvent<Element, Event>);
    },
    [isControlled, onClose]
  );

  return (
    <Tooltip
      {...props}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      {...(closedByScroll ? { TransitionProps: { ...props.TransitionProps, timeout: 0 }, leaveDelay: 0 } : null)}
    />
  );
}

export default ScrollAwareTooltip;
